import React from "react";
import ConfirmationModal from "components/shared/confirmation-modal";
import ImportSidePanel from "components/shared/import-side-panel";
import ExportSidePanel from "components/shared/export-side-panel";
import ExpandableList from "components/shared/expandable-list";
import { NamespaceKeys } from "locale/types";
import { FleetRow } from "../types";

interface ListModalsProps {
  t: (key: NamespaceKeys<"fleet">, options?: Record<string, unknown>) => string;
  // Delete
  showDeleteConfirmModal: boolean;
  setShowDeleteConfirmModal: (show: boolean) => void;
  deleteRow: FleetRow | null;
  onConfirmDelete: () => void;
  // Bulk
  showBulkConfirmModal: boolean;
  setShowBulkConfirmModal: (show: boolean) => void;
  bulkActionStatus: string;
  selectedKeys: (string | number)[];
  selectedPlateNumbers: string[];
  onConfirmBulkStatusUpdate: () => void;
  // Import
  isImportOpen: boolean;
  onCloseImport: () => void;
  onSubmitImport: () => void;
  onTemplateDownload: () => void;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  selectedFile: File | null;
  isImportLoading: boolean;
  // Import Status
  showImportStatusModal: boolean;
  setShowImportStatusModal: (show: boolean) => void;
  importStatusType: "success" | "warning" | "error";
  importStatusTitle: string;
  importStatusMessage: string | React.ReactNode;
  // Export
  isExportOpen: boolean;
  onCloseExport: () => void;
  onExport: () => void;
  exportFormat: string;
  exportType: string;
  onExportFormatChange: (format: string) => void;
  onExportTypeChange: (type: string) => void;
  isExportLoading: boolean;
  isBulkUpdating: boolean;
}

const ListModals: React.FC<ListModalsProps> = ({
  t,
  showDeleteConfirmModal,
  setShowDeleteConfirmModal,
  deleteRow,
  onConfirmDelete,
  showBulkConfirmModal,
  setShowBulkConfirmModal,
  bulkActionStatus,
  selectedKeys,
  selectedPlateNumbers,
  onConfirmBulkStatusUpdate,
  isImportOpen,
  onCloseImport,
  onSubmitImport,
  onTemplateDownload,
  onFileSelect,
  onFileRemove,
  selectedFile,
  isImportLoading,
  showImportStatusModal,
  setShowImportStatusModal,
  importStatusType,
  importStatusTitle,
  importStatusMessage,
  isExportOpen,
  onCloseExport,
  onExport,
  exportFormat,
  exportType,
  onExportFormatChange,
  onExportTypeChange,
  isExportLoading,
  isBulkUpdating,
}) => {
  return (
    <>
      {/* Unified Confirmation Modal handles all status changes and deletions */}

      <ConfirmationModal
        open={showBulkConfirmModal}
        title={(() => {
          if (bulkActionStatus === "Available") return t("bulk-activate-title");
          if (bulkActionStatus === "Inactive") return t("bulk-deactivate-title");
          if (bulkActionStatus === "Maintenance") return t("bulk-maintenance-title");
          if (bulkActionStatus === "Delete") return t("modal-delete-title");
          return "";
        })()}
        message={(() => {
          const count = selectedKeys.length;
          if (bulkActionStatus === "Available") return t("bulk-activate-message", { count });
          if (bulkActionStatus === "Inactive") return t("bulk-deactivate-message", { count });
          if (bulkActionStatus === "Maintenance") return t("bulk-maintenance-message", { count });
          if (bulkActionStatus === "Delete") return t("bulk-delete-message", { count });
          return "";
        })()}
        confirmLabel={(() => {
          if (bulkActionStatus === "Available") return t("bulk-activate-confirm");
          if (bulkActionStatus === "Inactive") return t("bulk-deactivate-confirm");
          if (bulkActionStatus === "Delete") return t("modal-ok-delete");
          if (bulkActionStatus === "Maintenance") return t("bulk-maintenance-confirm" as any);
          return t("btn-confirm");
        })()}
        cancelLabel={t("btn-cancel")}
        onConfirm={onConfirmBulkStatusUpdate}
        onCancel={() => setShowBulkConfirmModal(false)}
        icon={bulkActionStatus === "Delete" ? "danger" : "warning"}
        loading={isBulkUpdating}
        processingLabel={t("btn-processing" as any)}
      >
        <ExpandableList items={selectedPlateNumbers} />
      </ConfirmationModal>
      
      <ImportSidePanel
        open={isImportOpen}
        onClose={onCloseImport}
        onSubmit={onSubmitImport}
        onTemplateDownload={onTemplateDownload}
        onFileSelect={onFileSelect}
        onFileRemove={onFileRemove}
        selectedFile={selectedFile}
        submitLoading={isImportLoading}
        title={t("import-title")}
        subtitle={t("import-subtitle")}
        templateFileName="Fleet_Import_Template.xlsx"
      />

      <ConfirmationModal
        open={showImportStatusModal}
        onCancel={() => setShowImportStatusModal(false)}
        onConfirm={() => setShowImportStatusModal(false)}
        icon={importStatusType === "error" ? "danger" : importStatusType}
        title={importStatusTitle}
        message={importStatusMessage}
        confirmLabel={t("btn-close") || "Close"}
        hideCancelButton
      />

      <ExportSidePanel
        open={isExportOpen}
        onClose={onCloseExport}
        onSubmit={onExport}
        fileFormatValue={exportFormat}
        exportTypeValue={exportType}
        onFileFormatChange={onExportFormatChange}
        onExportTypeChange={onExportTypeChange}
        submitLoading={isExportLoading}
        fileFormatOptions={[
          { label: "Excel (.xlsx)", value: "xlsx" },
          { label: "CSV (.csv)", value: "csv" },
        ]}
        exportTypeOptions={[
          { label: t("export-all-data"), value: "all" },
          { label: t("export-filtered-data"), value: "filtered" },
          { label: t("export-selected-data"), value: "selected" },
        ]}
        title={t("export-title")}
        subtitle={t("export-subtitle")}
      />
    </>
  );
};

export default ListModals;
